import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InvoicesPage from './Invoices';
import { 
  collection, 
  query, 
  onSnapshot, 
  where, 
  orderBy, 
  limit,
  getDocs
} from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { useAuth } from '../App';
import { 
  Receipt, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  FileText, 
  TrendingUp,
  ArrowRight,
  Building2,
  Calendar,
  DollarSign,
  Plus,
  Printer,
  FileEdit,
  Send,
  Save,
  Trash2,
  X,
  Wrench,
  Search,
  Filter,
  Loader2,
  ClipboardList,
  Eye,
  Undo2,
  Download,
  ExternalLink,
  Paperclip
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { GenerateReportTrigger } from '../components/GenerateReportTrigger';
import { ServiceScheduleViewerModal } from '../components/ServiceScheduleViewerModal';
import { Invoice, Report, Client, ReportSparePart, ReportStatus, CashRequisition, JobCard, JobCardRequest } from '../types';
import { updateDoc, doc, Timestamp, addDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BillingDashboard: React.FC = () => {
  const { profile, isBilling, isManager, isAdmin, isHOD, isBillingHOD, isAccountantHOD } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pendingReports, setPendingReports] = useState<Report[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [requisitions, setRequisitions] = useState<CashRequisition[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [isPartSelectorOpen, setIsPartSelectorOpen] = useState(false);
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const [clientSpareParts, setClientSpareParts] = useState<any[]>([]);
  const [letterheadUrl, setLetterheadUrl] = useState<string | undefined>(undefined);
  const [isScheduleViewerOpen, setIsScheduleViewerOpen] = useState(false);

  // Job Cards & Bills Module States
  const [activeSection, setActiveSection] = useState<'billing' | 'invoices' | 'jobcards' | 'bills'>('billing');
  const [activeJobcardArticle, setActiveJobcardArticle] = useState<'create' | 'requests'>('create');
  const [jobCards, setJobCards] = useState<JobCard[]>([]);
  const [jobCardRequests, setJobCardRequests] = useState<JobCardRequest[]>([]);

  // Bills Module States
  const [billsSubTab, setBillsSubTab] = useState<'incoming' | 'report_after_service' | 'billed'>('incoming');
  const [billsSearchTerm, setBillsSearchTerm] = useState('');
  const [forwardedSurveys, setForwardedSurveys] = useState<any[]>([]);
  const [billedItems, setBilledItems] = useState<any[]>([]);
  const [customerCareReports, setCustomerCareReports] = useState<any[]>([]);

  // Create Bill Modal States
  const [isCreateBillModalOpen, setIsCreateBillModalOpen] = useState(false);
  const [selectedIncomingReport, setSelectedIncomingReport] = useState<any | null>(null);
  const [selectedProformaIds, setSelectedProformaIds] = useState<string[]>([]);
  const [isManualProforma, setIsManualProforma] = useState(false);
  const [manualProformaNo, setManualProformaNo] = useState('');
  const [billAmount, setBillAmount] = useState<number>(0);
  const [billDate, setBillDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [billRemarks, setBillRemarks] = useState('');
  const [attachedInvoiceUrl, setAttachedInvoiceUrl] = useState('');
  const [proformaSearch, setProformaSearch] = useState('');
  const [isSubmittingBill, setIsSubmittingBill] = useState(false);
  const [viewingSurveyReport, setViewingSurveyReport] = useState<any | null>(null);
  const [viewingBilledInvoice, setViewingBilledInvoice] = useState<any | null>(null);
  const [proformaModalTab, setProformaModalTab] = useState<'attached' | 'document'>('attached');

  useEffect(() => {
    if (viewingBilledInvoice) {
      if (viewingBilledInvoice.attachedInvoiceUrl) {
        setProformaModalTab('attached');
      } else {
        setProformaModalTab('document');
      }
    }
  }, [viewingBilledInvoice]);
  
  // Job Card Creation / Editing states
  const [isJobCardModalOpen, setIsJobCardModalOpen] = useState(false);
  const [jobCardFormStep, setJobCardFormStep] = useState(1);
  const [editingJobCardId, setEditingJobCardId] = useState<string | null>(null);

  // Form states for Jobcard Step 1
  const [jcClientId, setJcClientId] = useState('');
  const [jcLpoNo, setJcLpoNo] = useState('');
  const [jcLpoDate, setJcLpoDate] = useState('');
  const [jcProformaNo, setJcProformaNo] = useState('');
  const [jcProformaDate, setJcProformaDate] = useState('');
  const [jcProformaAmount, setJcProformaAmount] = useState(0);
  const [jcCustomerRequest, setJcCustomerRequest] = useState('');
  
  // Form states for Jobcard Step 2
  const [jcServiceMemoNo, setJcServiceMemoNo] = useState('');
  const [jcServiceMemoDate, setJcServiceMemoDate] = useState('');
  const [jcInvoiceNo, setJcInvoiceNo] = useState('');
  const [jcInvoiceAmount, setJcInvoiceAmount] = useState(0);
  const [jcInvoiceDate, setJcInvoiceDate] = useState('');
  const [jcJobTerms, setJcJobTerms] = useState<'Contract' | 'LPO' | 'Quotation' | 'Verbal' | 'Email'>('Verbal');
  const [jcQuotationRef, setJcQuotationRef] = useState('');
  const [jcQuotationDate, setJcQuotationDate] = useState('');
  const [jcIsForService, setJcIsForService] = useState<'True' | 'False'>('False');
  
  // Form states for Jobcard Step 3
  const [jcTechnicalReport, setJcTechnicalReport] = useState('');
  const [jcWorkdoneDetails, setJcWorkdoneDetails] = useState('');

  // Link jobcard state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<JobCardRequest | null>(null);
  const [selectedJcNoToLink, setSelectedJcNoToLink] = useState('');
  const [isSubmittingLink, setIsSubmittingLink] = useState(false);

  useEffect(() => {
    let invoicesQuery = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    
    // Data isolation logic
    const canSeeAll = isAdmin || isManager || (isHOD && (isBillingHOD || isAccountantHOD));
    if (!canSeeAll && profile?.uid) {
      invoicesQuery = query(
        collection(db, 'invoices'),
        where('creatorId', '==', profile.uid),
        orderBy('createdAt', 'desc')
      );
    }

    const invoicesUnsubscribe = onSnapshot(invoicesQuery, (snapshot) => {
      const invoicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice));
      setInvoices(invoicesData);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'invoices');
    });

    const reportsQuery = query(
      collection(db, 'reports'), 
      where('status', 'in', ['submitted_to_billing', 'technical_survey_report'])
    );
    const reportsUnsubscribe = onSnapshot(reportsQuery, (snapshot) => {
      setPendingReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    const clientsUnsubscribe = onSnapshot(collection(db, 'clients'), (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    const settingsUnsubscribe = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLetterheadUrl(snapshot.docs[0].data().letterheadUrl);
      }
    });

    const requisitionsQuery = query(collection(db, 'cash_requisitions'), orderBy('createdAt', 'desc'), limit(20));
    const requisitionsUnsubscribe = onSnapshot(requisitionsQuery, (snapshot) => {
      setRequisitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CashRequisition)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'cash_requisitions');
    });

    const jobCardsQuery = query(collection(db, 'job_cards'), orderBy('createdAt', 'desc'));
    const jobCardsUnsubscribe = onSnapshot(jobCardsQuery, (snapshot) => {
      setJobCards(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JobCard)));
    });

    const jobCardRequestsQuery = query(collection(db, 'job_card_requests'), orderBy('createdAt', 'desc'));
    const jobCardRequestsUnsubscribe = onSnapshot(jobCardRequestsQuery, (snapshot) => {
      setJobCardRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as JobCardRequest)));
    });

    // 1. Listen for site_surveys forwarded to Billing
    const siteSurveysUnsubscribe = onSnapshot(collection(db, 'site_surveys'), (snapshot) => {
      const surveys = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setForwardedSurveys(surveys.filter((s: any) => 
        s.status === 'Forwarded' || 
        s.status === 'submitted_to_billing' || 
        s.userDepartment === 'Billing & Commercial Department' ||
        s.forwardedTo === 'Billing & Commercial Department' ||
        s.status === 'On Progress' || s.status === 'Completed'
      ));
    });

    // 2. Listen for bills
    const billsUnsubscribe = onSnapshot(query(collection(db, 'bills'), orderBy('createdAt', 'desc')), (snapshot) => {
      setBilledItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // 3. Listen for customer care tasks/reports
    const customerCareUnsubscribe = onSnapshot(query(collection(db, 'customer_care_tasks'), orderBy('createdAt', 'desc')), (snapshot) => {
      setCustomerCareReports(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      invoicesUnsubscribe();
      reportsUnsubscribe();
      clientsUnsubscribe();
      settingsUnsubscribe();
      requisitionsUnsubscribe();
      jobCardsUnsubscribe();
      jobCardRequestsUnsubscribe();
      siteSurveysUnsubscribe();
      billsUnsubscribe();
      customerCareUnsubscribe();
    };
  }, []);

  // Auto-calculate bill amount when selectedProformaIds changes
  useEffect(() => {
    if (!isManualProforma && selectedProformaIds.length > 0) {
      const total = selectedProformaIds.reduce((sum, id) => {
        const inv = invoices.find(i => i.id === id);
        return sum + (inv ? inv.totalAmount : 0);
      }, 0);
      setBillAmount(total);
    }
  }, [selectedProformaIds, isManualProforma, invoices]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedInvoiceUrl(reader.result as string);
        toast.success(`Attached invoice file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateBillSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIncomingReport) return;

    let proformaNoStr = '';
    if (isManualProforma) {
      if (!manualProformaNo.trim()) {
        toast.error('Please enter a Proforma Invoice Number');
        return;
      }
      proformaNoStr = manualProformaNo.trim();
    } else {
      if (selectedProformaIds.length === 0) {
        toast.error('Please select at least one Proforma Invoice');
        return;
      }
      proformaNoStr = selectedProformaIds.map(id => {
        const inv = invoices.find(i => i.id === id);
        return inv ? inv.invoiceNumber : id;
      }).join(', ');
    }

    if (!billAmount || billAmount <= 0) {
      toast.error('Please enter a valid Bill Amount');
      return;
    }

    setIsSubmittingBill(true);
    try {
      const billData = {
        reportId: selectedIncomingReport.id,
        siteName: selectedIncomingReport.siteName || selectedIncomingReport.clientName || 'Site',
        clientName: selectedIncomingReport.clientName || selectedIncomingReport.siteName || 'Client',
        proformaNo: proformaNoStr,
        billAmount: Number(billAmount),
        billDate: billDate || new Date().toISOString().split('T')[0],
        remarks: billRemarks || '',
        attachedInvoiceUrl: attachedInvoiceUrl || '',
        status: 'Billed',
        billedBy: profile?.displayName || profile?.email || 'Billing Staff',
        billedById: profile?.uid || 'unknown',
        createdAt: Timestamp.now(),
        machineType: selectedIncomingReport.machineType || 'Air Conditioner',
        technicianName: selectedIncomingReport.technicianName || 'Technician',
        clientRequest: selectedIncomingReport.clientRequest || selectedIncomingReport.title || selectedIncomingReport.findings || '',
        checkingResult: selectedIncomingReport.checkingResult || selectedIncomingReport.findings || '',
        remedialRecommendation: selectedIncomingReport.remedialRecommendation || selectedIncomingReport.recommendations || ''
      };

      // 1. Create bill in Firestore
      await addDoc(collection(db, 'bills'), billData);

      // 2. Add to technical_reports so it appears under Active Technical Report in Technical Department
      await addDoc(collection(db, 'technical_reports'), {
        siteName: billData.siteName,
        clientRequest: billData.clientRequest,
        proformaNo: billData.proformaNo,
        machineType: billData.machineType,
        technicianName: billData.technicianName,
        checkingResult: billData.checkingResult,
        remedialRecommendation: billData.remedialRecommendation,
        status: 'active', // Important: Moves to Active Technical report in Technical Dashboard!
        createdAt: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
        createdAtTimestamp: Timestamp.now(),
        billedAmount: billData.billAmount,
        billedBy: billData.billedBy,
        reportId: selectedIncomingReport.id
      });

      // 3. Update original survey report status
      try {
        await updateDoc(doc(db, 'site_surveys', selectedIncomingReport.id), {
          status: 'Billed',
          proformaNo: billData.proformaNo,
          updatedAt: Timestamp.now()
        });
      } catch (err) {
        try {
          await updateDoc(doc(db, 'reports', selectedIncomingReport.id), {
            status: 'invoiced',
            updatedAt: Timestamp.now()
          });
        } catch (e2) {
          console.log('Doc update fallback:', e2);
        }
      }

      toast.success(`Bill created! Proforma #${proformaNoStr} returned to Technical Active Reports.`);
      setIsCreateBillModalOpen(false);
      setSelectedIncomingReport(null);
      setSelectedProformaIds([]);
      setManualProformaNo('');
      setBillAmount(0);
      setBillRemarks('');
      setAttachedInvoiceUrl('');
    } catch (error) {
      console.error('Error creating bill:', error);
      toast.error('Failed to create bill. Please try again.');
    } finally {
      setIsSubmittingBill(false);
    }
  };

  useEffect(() => {
    if (selectedReport) {
      // Fetch client spare parts from their reports and dedicated collection
      const reportsQuery = query(collection(db, 'reports'), where('clientId', '==', selectedReport.clientId));
      const sparePartsRef = collection(db, 'clients', selectedReport.clientId, 'spareParts');
      
      const unsubscribeParts = onSnapshot(reportsQuery, (snapshot) => {
        const reportParts: any[] = [];
        snapshot.docs.forEach(doc => {
          const report = doc.data() as Report;
          if (report.requiredSpareParts) {
            report.requiredSpareParts.forEach(p => {
              if (!reportParts.find(existing => existing.name === p.name)) {
                reportParts.push({ ...p, id: `report-${p.name}` });
              }
            });
          }
        });
        
        // Also fetch from dedicated collection
        const unsubscribeDedicated = onSnapshot(sparePartsRef, (spareSnapshot) => {
          const dedicatedParts = spareSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
          const allParts = [...reportParts];
          dedicatedParts.forEach(p => {
            if (!allParts.find(existing => (existing as any).name === p.name)) {
              allParts.push(p);
            }
          });
          setClientSpareParts(allParts);
        });

        return () => unsubscribeDedicated();
      });

      return () => unsubscribeParts();
    }
  }, [selectedReport]);

  const handleConvertReport = (report: Report) => {
    setSelectedReport({
      ...report,
      labourCharge: report.labourCharge || 0,
      requiredSpareParts: report.requiredSpareParts?.map(p => ({ ...p, price: p.price || 0 })) || []
    });
    setIsSurveyModalOpen(true);
  };

  const handleStatusChange = async (newStatus: ReportStatus) => {
    if (!selectedReport) return;
    try {
      await updateDoc(doc(db, 'reports', selectedReport.id), {
        ...selectedReport,
        status: newStatus,
        updatedAt: Timestamp.now()
      });
      toast.success(`Report status updated to ${newStatus.replace(/_/g, ' ')}`);
      setIsSurveyModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `reports/${selectedReport.id}`);
      toast.error('Failed to update report status');
    }
  };

  const resetJobCardForm = () => {
    setEditingJobCardId(null);
    setJobCardFormStep(1);
    setJcClientId('');
    setJcLpoNo('');
    setJcLpoDate('');
    setJcProformaNo('');
    setJcProformaDate('');
    setJcProformaAmount(0);
    setJcCustomerRequest('');
    setJcServiceMemoNo('');
    setJcServiceMemoDate('');
    setJcInvoiceNo('');
    setJcInvoiceAmount(0);
    setJcInvoiceDate('');
    setJcJobTerms('Verbal');
    setJcQuotationRef('');
    setJcQuotationDate('');
    setJcIsForService('False');
    setJcTechnicalReport('');
    setJcWorkdoneDetails('');
  };

  const handleOpenNewJobCard = () => {
    resetJobCardForm();
    setIsJobCardModalOpen(true);
  };

  const handleEditJobCard = (card: JobCard) => {
    setEditingJobCardId(card.id);
    setJobCardFormStep(1);
    setJcClientId(card.clientId);
    setJcLpoNo(card.lpoNo || '');
    setJcLpoDate(card.lpoDate || '');
    setJcProformaNo(card.proformaNo || '');
    setJcProformaDate(card.proformaDate || '');
    setJcProformaAmount(card.proformaAmount || 0);
    setJcCustomerRequest(card.customerRequest || '');
    setJcServiceMemoNo(card.serviceMemoNo || '');
    setJcServiceMemoDate(card.serviceMemoDate || '');
    setJcInvoiceNo(card.invoiceNo || '');
    setJcInvoiceAmount(card.invoiceAmount || 0);
    setJcInvoiceDate(card.invoiceDate || '');
    setJcJobTerms(card.jobTerms || 'Verbal');
    setJcQuotationRef(card.quotationRef || '');
    setJcQuotationDate(card.quotationDate || '');
    setJcIsForService(card.isForService || 'False');
    setJcTechnicalReport(card.technicalReport || '');
    setJcWorkdoneDetails(card.workdoneDetails || '');
    setIsJobCardModalOpen(true);
  };

  const handleDeleteJobCard = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this job card?')) {
      try {
        await deleteDoc(doc(db, 'job_cards', id));
        toast.success('Job card deleted successfully!');
      } catch (error) {
        console.error('Error deleting job card:', error);
        toast.error('Failed to delete job card');
      }
    }
  };

  const handleSaveJobCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jcClientId) {
      toast.error('Please select a client');
      return;
    }
    if (!jcCustomerRequest.trim()) {
      toast.error('Customer Request is required');
      return;
    }

    try {
      let finalJobCardNo = '';
      if (editingJobCardId) {
        const existingCard = jobCards.find(c => c.id === editingJobCardId);
        finalJobCardNo = existingCard ? existingCard.jobCardNo : '';
      } else {
        let nextNum = 4678;
        if (jobCards.length > 0) {
          const nums = jobCards.map(c => parseInt(c.jobCardNo)).filter(n => !isNaN(n));
          if (nums.length > 0) {
            nextNum = Math.max(...nums) + 1;
          }
        }
        finalJobCardNo = nextNum.toString().padStart(8, '0');
      }

      const selectedClient = clients.find(c => c.id === jcClientId);
      const clientName = selectedClient ? selectedClient.name : 'Unknown Client';

      const cardData = {
        jobCardNo: finalJobCardNo,
        clientId: jcClientId,
        clientName,
        lpoNo: jcLpoNo,
        lpoDate: jcLpoDate,
        proformaNo: jcProformaNo,
        proformaDate: jcProformaDate,
        proformaAmount: Number(jcProformaAmount),
        customerRequest: jcCustomerRequest,
        serviceMemoNo: jcServiceMemoNo,
        serviceMemoDate: jcServiceMemoDate,
        invoiceNo: jcInvoiceNo,
        invoiceAmount: Number(jcInvoiceAmount),
        invoiceDate: jcInvoiceDate,
        jobTerms: jcJobTerms,
        quotationRef: jcQuotationRef,
        quotationDate: jcQuotationDate,
        isForService: jcIsForService,
        technicalReport: jcTechnicalReport,
        workdoneDetails: jcWorkdoneDetails,
        createdAt: Timestamp.now(),
        createdBy: profile?.displayName || profile?.email || 'Billing Staff',
        creatorId: profile?.uid || 'unknown'
      };

      if (editingJobCardId) {
        await updateDoc(doc(db, 'job_cards', editingJobCardId), cardData as any);
        toast.success(`Job Card ${finalJobCardNo} updated successfully!`);
      } else {
        await addDoc(collection(db, 'job_cards'), cardData);
        toast.success(`Job Card ${finalJobCardNo} created successfully!`);
      }
      setIsJobCardModalOpen(false);
      resetJobCardForm();
    } catch (error) {
      console.error('Error saving job card:', error);
      toast.error('Failed to save job card');
    }
  };

  const handleLinkJobCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRequest || !selectedJcNoToLink) {
      toast.error('Please select a Job Card to link');
      return;
    }

    const linkedJc = jobCards.find(jc => jc.jobCardNo === selectedJcNoToLink);
    if (!linkedJc) {
      toast.error('Selected Job Card details not found');
      return;
    }

    setIsSubmittingLink(true);
    try {
      await updateDoc(doc(db, 'job_card_requests', selectedRequest.id), {
        status: 'completed',
        jobCardNo: linkedJc.jobCardNo,
        proformaNo: linkedJc.proformaNo,
        proformaAmount: linkedJc.proformaAmount,
        proformaDate: linkedJc.proformaDate,
        lpoNo: linkedJc.lpoNo,
        lpoDate: linkedJc.lpoDate,
        description: linkedJc.customerRequest,
        jobTerms: linkedJc.jobTerms,
        isForService: linkedJc.isForService,
        updatedAt: Timestamp.now()
      });

      toast.success(`Linked Job Card ${linkedJc.jobCardNo} to request and submitted details back to Technical!`);
      setIsLinkModalOpen(false);
      setSelectedRequest(null);
      setSelectedJcNoToLink('');
    } catch (error) {
      console.error('Error linking job card:', error);
      toast.error('Failed to link Job Card');
    } finally {
      setIsSubmittingLink(false);
    }
  };

  const handleSaveSurveyReport = async () => {
    if (!selectedReport) return;
    try {
      await updateDoc(doc(db, 'reports', selectedReport.id), {
        ...selectedReport,
        status: 'technical_survey_report',
        updatedAt: Timestamp.now()
      });
      toast.success('Technical Survey Report saved');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `reports/${selectedReport.id}`);
      toast.error('Failed to save report');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!selectedReport) return;
    if (!window.confirm('Convert this Technical Survey Report to a Proforma Invoice?')) return;

    try {
      const totalSpareParts = (selectedReport.requiredSpareParts || []).reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0);
      const totalAmount = totalSpareParts + (selectedReport.labourCharge || 0);

      const invoiceData: Omit<Invoice, 'id'> & { creatorId: string; creatorEmail: string } = {
        invoiceNumber: `INV-${Date.now()}`,
        clientId: selectedReport.clientId,
        clientName: selectedReport.clientName || '',
        branchId: selectedReport.branchId,
        branchName: selectedReport.branchName,
        reportId: selectedReport.id,
        items: [
          ...(selectedReport.requiredSpareParts || []).map(p => ({
            description: p.name,
            quantity: p.quantity,
            unitPrice: p.price || 0,
            amount: p.quantity * (p.price || 0)
          })),
          {
            description: 'Labour Charge',
            quantity: 1,
            unitPrice: selectedReport.labourCharge || 0,
            amount: selectedReport.labourCharge || 0
          }
        ],
        totalAmount: totalAmount,
        type: 'proforma',
        status: 'draft',
        currency: 'TZS',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        dueDate: Timestamp.fromDate(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)),
        creatorId: profile?.uid || '',
        creatorEmail: profile?.email || ''
      };

      await addDoc(collection(db, 'invoices'), invoiceData);
      await updateDoc(doc(db, 'reports', selectedReport.id), {
        status: 'invoiced',
        updatedAt: Timestamp.now()
      });

      toast.success('Proforma Invoice created successfully');
      setIsSurveyModalOpen(false);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice');
    }
  };

  const addSparePart = (part: any) => {
    if (!selectedReport) return;
    const newParts = [...(selectedReport.requiredSpareParts || []), { 
      name: part.name, 
      quantity: 1, 
      price: part.price || 0,
      unit: part.unit
    }];
    setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
  };

  const removeSparePart = (index: number) => {
    if (!selectedReport) return;
    const newParts = selectedReport.requiredSpareParts?.filter((_, i) => i !== index);
    setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
  };

  const updateSparePart = (index: number, field: keyof ReportSparePart, value: any) => {
    if (!selectedReport || !selectedReport.requiredSpareParts) return;
    const newParts = [...selectedReport.requiredSpareParts];
    newParts[index] = { ...newParts[index], [field]: value };
    setSelectedReport({ ...selectedReport, requiredSpareParts: newParts });
  };

  const printSurveyReport = (report: Report) => {
    const doc = new jsPDF();
    
    // Header
    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 0, 0, 210, 40);
      } catch (e) {
        doc.setFontSize(20);
        doc.setTextColor(0, 51, 102);
        doc.text('TECHNICAL SURVEY REPORT', 105, 20, { align: 'center' });
      }
    } else {
      doc.setFontSize(20);
      doc.setTextColor(0, 51, 102);
      doc.text('TECHNICAL SURVEY REPORT', 105, 20, { align: 'center' });
    }
    
    const startY = letterheadUrl ? 50 : 30;

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 195, startY, { align: 'right' });
    
    // Client Info
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('CLIENT INFORMATION', 20, startY + 10);
    doc.setFontSize(10);
    doc.text(`Client: ${report.clientName}`, 20, startY + 18);
    doc.text(`Branch: ${report.branchName || 'N/A'}`, 20, startY + 24);
    doc.text(`Technician: ${report.technicianName}`, 20, startY + 30);

    // Findings
    doc.setFontSize(12);
    doc.text('FINDINGS', 20, startY + 45);
    doc.setFontSize(10);
    const findingsLines = doc.splitTextToSize(report.findings || '', 170);
    doc.text(findingsLines, 20, startY + 53);

    // Recommendations
    const recommendationsY = startY + 53 + (findingsLines.length * 5) + 10;
    doc.setFontSize(12);
    doc.text('RECOMMENDATIONS', 20, recommendationsY);
    doc.setFontSize(10);
    const recLines = doc.splitTextToSize(report.recommendations || '', 170);
    doc.text(recLines, 20, recommendationsY + 8);

    // Spare Parts Table
    const tableY = recommendationsY + 8 + (recLines.length * 5) + 10;
    autoTable(doc, {
      startY: tableY,
      head: [['Spare Part', 'Quantity', 'Unit Price', 'Total']],
      body: [
        ...(report.requiredSpareParts || []).map(p => [
          p.name,
          p.quantity,
          formatCurrency(p.price || 0),
          formatCurrency(p.quantity * (p.price || 0))
        ]),
        ['Labour Charge', '-', '-', formatCurrency(report.labourCharge || 0)]
      ],
      foot: [['', '', 'TOTAL', formatCurrency((report.requiredSpareParts || []).reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0) + (report.labourCharge || 0))]],
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    downloadFile(doc.output('blob'), `Survey_Report_${report.clientName}_${Date.now()}.pdf`);
  };

  const handleUndoBilled = async (bill: any) => {
    if (!window.confirm(`Are you sure you want to undo billing for Proforma #${bill.proformaNo || ''}? This will return the survey report to Incoming and clean up generated technical reports.`)) {
      return;
    }

    try {
      // 1. Delete record from 'bills' collection
      if (bill.id) {
        await deleteDoc(doc(db, 'bills', bill.id));
      }

      // 2. Return original survey report back to 'submitted_to_billing'
      const reportIdToRestore = bill.reportId || bill.id;

      if (reportIdToRestore) {
        try {
          await updateDoc(doc(db, 'site_surveys', reportIdToRestore), {
            status: 'submitted_to_billing',
            proformaNo: '',
            updatedAt: Timestamp.now()
          });
        } catch (e1) {
          console.log('site_surveys update fallback:', e1);
        }

        try {
          await updateDoc(doc(db, 'reports', reportIdToRestore), {
            status: 'submitted_to_billing',
            proformaNo: '',
            updatedAt: Timestamp.now()
          });
        } catch (e2) {
          console.log('reports update fallback:', e2);
        }
      }

      // 3. Query site_surveys & reports by proformaNo to ensure all matching records are returned to Incoming
      if (bill.proformaNo) {
        try {
          const qSurvey = query(collection(db, 'site_surveys'), where('proformaNo', '==', bill.proformaNo));
          const surveySnap = await getDocs(qSurvey);
          for (const d of surveySnap.docs) {
            await updateDoc(doc(db, 'site_surveys', d.id), {
              status: 'submitted_to_billing',
              proformaNo: '',
              updatedAt: Timestamp.now()
            });
          }
        } catch (eSurv) {
          console.log('site_surveys query fallback:', eSurv);
        }

        try {
          const qReports = query(collection(db, 'reports'), where('proformaNo', '==', bill.proformaNo));
          const reportSnap = await getDocs(qReports);
          for (const d of reportSnap.docs) {
            await updateDoc(doc(db, 'reports', d.id), {
              status: 'submitted_to_billing',
              proformaNo: '',
              updatedAt: Timestamp.now()
            });
          }
        } catch (eRep) {
          console.log('reports query fallback:', eRep);
        }
      }

      // 4. Delete from technical_reports (by reportId and proformaNo)
      try {
        if (bill.reportId) {
          const qTech = query(collection(db, 'technical_reports'), where('reportId', '==', bill.reportId));
          const techSnap = await getDocs(qTech);
          for (const d of techSnap.docs) {
            await deleteDoc(doc(db, 'technical_reports', d.id));
          }
        }
        if (bill.proformaNo) {
          const qTech2 = query(collection(db, 'technical_reports'), where('proformaNo', '==', bill.proformaNo));
          const techSnap2 = await getDocs(qTech2);
          for (const d of techSnap2.docs) {
            await deleteDoc(doc(db, 'technical_reports', d.id));
          }
        }
      } catch (e3) {
        console.log('technical_reports fallback:', e3);
      }

      // 5. Delete from invoices matching proformaNo
      if (bill.proformaNo) {
        try {
          const qInv = query(collection(db, 'invoices'), where('invoiceNumber', '==', bill.proformaNo));
          const invSnap = await getDocs(qInv);
          for (const d of invSnap.docs) {
            await deleteDoc(doc(db, 'invoices', d.id));
          }
        } catch (eInv) {
          console.log('invoices fallback:', eInv);
        }
      }

      toast.success(`Billing undone! Report for "${bill.siteName || bill.clientName || 'Site'}" returned to Incoming.`);
    } catch (error) {
      console.error('Error undoing bill:', error);
      toast.error('Failed to undo billing. Please try again.');
    }
  };

  const downloadSurveyReportPDF = (report: any) => {
    const doc = new jsPDF();
    
    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
      } catch (e) {
        doc.setFontSize(18);
        doc.setTextColor(0, 51, 102);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
        doc.setFontSize(13);
        doc.text('TECHNICAL SURVEY REPORT', 105, 26, { align: 'center' });
      }
    } else {
      doc.setFontSize(18);
      doc.setTextColor(0, 51, 102);
      doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
      doc.setFontSize(13);
      doc.text('TECHNICAL SURVEY REPORT', 105, 26, { align: 'center' });
    }

    const startY = letterheadUrl ? 45 : 35;

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Date: ${report.createdAt || new Date().toLocaleDateString()}`, 195, startY, { align: 'right' });

    doc.setFontSize(11);
    doc.setTextColor(0, 51, 102);
    doc.setFont('helvetica', 'bold');
    doc.text('TECHNICAL SURVEY REPORT DETAILS', 15, startY + 8);

    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 51, 102);
    doc.line(15, startY + 10, 195, startY + 10);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30);

    doc.text(`Site / Client Name: ${report.siteName || report.clientName || 'General Site'}`, 15, startY + 18);
    doc.text(`Technician Name: ${report.technicianName || 'Field Technician'}`, 15, startY + 24);
    doc.text(`Machine / Equipment: ${report.machineType || 'Air Conditioner'}`, 15, startY + 30);

    let currY = startY + 40;
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT REQUEST / SCOPE OF WORK:', 15, currY);
    doc.setFont('helvetica', 'normal');
    const reqLines = doc.splitTextToSize(report.clientRequest || report.title || report.findings || 'N/A', 175);
    doc.text(reqLines, 15, currY + 6);

    currY = currY + 10 + (reqLines.length * 5);
    doc.setFont('helvetica', 'bold');
    doc.text('CHECKING RESULT / FINDINGS:', 15, currY);
    doc.setFont('helvetica', 'normal');
    const checkLines = doc.splitTextToSize(report.checkingResult || report.findings || 'N/A', 175);
    doc.text(checkLines, 15, currY + 6);

    currY = currY + 10 + (checkLines.length * 5);
    doc.setFont('helvetica', 'bold');
    doc.text('REMEDIAL RECOMMENDATION:', 15, currY);
    doc.setFont('helvetica', 'normal');
    const recLines = doc.splitTextToSize(report.remedialRecommendation || report.recommendations || 'N/A', 175);
    doc.text(recLines, 15, currY + 6);

    if (report.requiredSpareParts && report.requiredSpareParts.length > 0) {
      currY = currY + 10 + (recLines.length * 5);
      autoTable(doc, {
        startY: currY,
        head: [['Spare Part Name', 'Quantity', 'Unit Price', 'Total']],
        body: report.requiredSpareParts.map((p: any) => [
          p.name,
          p.quantity,
          formatCurrency(p.price || 0),
          formatCurrency((p.quantity || 1) * (p.price || 0))
        ]),
        theme: 'grid',
        headStyles: { fillColor: [0, 51, 102] }
      });
    }

    downloadFile(doc.output('blob'), `Technical_Survey_Report_${(report.siteName || report.clientName || 'Report').replace(/\s+/g, '_')}.pdf`);
  };

  const downloadProformaInvoicePDF = (bill: any) => {
    const doc = new jsPDF();
    
    // Find matching invoice from invoices state if present
    const matchedInv = invoices.find(inv => 
      (inv.invoiceNumber && bill.proformaNo && String(inv.invoiceNumber).toLowerCase().trim() === String(bill.proformaNo).toLowerCase().trim()) ||
      (inv.id && bill.proformaNo && inv.id === bill.proformaNo) ||
      (bill.proformaNo && inv.invoiceNumber && String(bill.proformaNo).toLowerCase().includes(inv.invoiceNumber.toLowerCase())) ||
      (bill.reportId && inv.reportId === bill.reportId)
    );

    if (letterheadUrl) {
      try {
        doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
      } catch (e) {
        doc.setFontSize(18);
        doc.setTextColor(0, 51, 102);
        doc.text('BRIGHT TECHNICAL SYSTEMS', 105, 18, { align: 'center' });
        doc.setFontSize(13);
        doc.text('PROFORMA INVOICE', 105, 26, { align: 'center' });
      }
    } else {
      doc.setFontSize(18);
      doc.setTextColor(0, 51, 102);
      doc.text('BRIGHT TECHNICAL SYSTEMS & GENERAL SUPPLIES LTD', 105, 18, { align: 'center' });
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text('Air Conditioning, Refrigeration & Electrical Engineering Specialists', 105, 24, { align: 'center' });
      doc.setFontSize(9);
      doc.text('TIN: 123-456-789 | VRN: 987-654-321 | Tel: +255 700 000 000 | Email: contact@bts.com', 105, 29, { align: 'center' });
    }

    const startY = letterheadUrl ? 45 : 36;

    doc.setFontSize(14);
    doc.setTextColor(0, 51, 102);
    doc.setFont('helvetica', 'bold');
    doc.text(`PROFORMA INVOICE #${bill.proformaNo}`, 15, startY + 6);

    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date Issued: ${bill.billDate || new Date().toLocaleDateString('en-GB')}`, 195, startY + 6, { align: 'right' });

    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 51, 102);
    doc.line(15, startY + 10, 195, startY + 10);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30);
    doc.text('BILL TO:', 15, startY + 18);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`${bill.siteName || bill.clientName || 'N/A'}`, 15, startY + 24);
    doc.text(`Equipment / Scope: ${bill.machineType || 'Technical Services'}`, 15, startY + 30);
    doc.text(`Sales Agent / Billed By: ${bill.billedBy || 'Billing Staff'}`, 15, startY + 36);

    if (bill.clientRequest || matchedInv?.description) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Subject: RE: ${(bill.clientRequest || matchedInv?.description || '').toUpperCase()}`, 15, startY + 44);
    }

    // Construct items for table
    const tableBody = matchedInv && matchedInv.items && matchedInv.items.length > 0
      ? matchedInv.items.map((item, index) => [
          item.serialNumber || (index + 1).toString(),
          item.description,
          `${item.quantity} ${item.unit || 'pcs'}`,
          `TZS ${Number(item.unitPrice).toLocaleString()}`,
          `TZS ${Number(item.amount).toLocaleString()}`
        ])
      : [
          [
            '1',
            bill.clientRequest || 'Technical Survey & Service Billing',
            '1 Job',
            `TZS ${Number(bill.billAmount).toLocaleString()}`,
            `TZS ${Number(bill.billAmount).toLocaleString()}`
          ]
        ];

    const tableStartY = (bill.clientRequest || matchedInv?.description) ? startY + 50 : startY + 42;

    autoTable(doc, {
      startY: tableStartY,
      head: [['S/N', 'Item / Service Description', 'Qty', 'Unit Price', 'Total Amount']],
      body: tableBody,
      foot: [['', '', '', 'TOTAL AMOUNT', `TZS ${Number(matchedInv?.totalAmount || bill.billAmount).toLocaleString()}`]],
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontStyle: 'bold' },
      footStyles: { fillColor: [240, 243, 246], textColor: [0, 51, 102], fontStyle: 'bold' }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;

    // Payment Details Section
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 51, 102);
    doc.text('PAYMENT BANK DETAILS:', 15, finalY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50);
    doc.text('Bank Name: CRDB Bank Ltd | Account Name: Bright Technical Systems & General Supplies Ltd', 15, finalY + 5);
    doc.text('Account Number: 0150234567800 | Branch: Dar es Salaam | Currency: TZS', 15, finalY + 10);

    if (bill.remarks) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 51, 102);
      doc.text('Remarks / Notes:', 15, finalY + 18);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(50);
      const remarkLines = doc.splitTextToSize(bill.remarks, 175);
      doc.text(remarkLines, 15, finalY + 23);
    }

    // Signatures
    const sigY = finalY + (bill.remarks ? 35 : 22);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Prepared By:', 15, sigY);
    doc.line(15, sigY + 12, 65, sigY + 12);
    doc.setFont('helvetica', 'normal');
    doc.text(bill.billedBy || 'Billing Staff', 15, sigY + 17);

    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signature & Stamp:', 130, sigY);
    doc.line(130, sigY + 12, 190, sigY + 12);
    doc.setFont('helvetica', 'normal');
    doc.text('Bright Technical Systems', 130, sigY + 17);

    downloadFile(doc.output('blob'), `Proforma_Invoice_${bill.proformaNo}.pdf`);
  };

  const stats = {
    totalInvoiced: invoices.reduce((sum, inv) => sum + inv.totalAmount, 0),
    pendingAmount: invoices.filter(inv => inv.status === 'sent').reduce((sum, inv) => sum + inv.totalAmount, 0),
    paidAmount: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.totalAmount, 0),
    overdueAmount: invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.totalAmount, 0),
    pendingCount: invoices.filter(inv => inv.status === 'sent').length,
    overdueCount: invoices.filter(inv => inv.status === 'overdue').length,
  };

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'TZS' ? 'TZS' : currency,
      minimumFractionDigits: 0,
    }).format(amount).replace('TZS', 'TSh');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Billing Dashboard</h1>
          <p className="text-slate-500 font-medium">Manage invoices, payments and billing workflows.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GenerateReportTrigger />
          <button
            onClick={() => setIsScheduleViewerOpen(true)}
            className="flex items-center gap-2 px-5 py-3 bg-indigo-900 text-white rounded-2xl font-bold hover:bg-indigo-800 transition-all shadow-md shadow-indigo-900/20 active:scale-95 cursor-pointer text-xs uppercase tracking-wider"
          >
            <Calendar className="w-4 h-4 text-indigo-300" />
            <span>View Service Schedule</span>
          </button>
          {activeSection === 'billing' && (
            <Link
              to="/invoices"
              className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20"
            >
              <Plus className="w-5 h-5" />
              <span>New Invoice</span>
            </Link>
          )}
        </div>
      </div>

      {/* Main Section Navigation Tab Bar */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 w-fit flex-wrap gap-1">
        <button
          onClick={() => setActiveSection('billing')}
          className={cn(
            "px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all cursor-pointer",
            activeSection === 'billing' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
          )}
        >
          Billing Overview
        </button>
        <button
          onClick={() => setActiveSection('invoices')}
          className={cn(
            "px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all cursor-pointer",
            activeSection === 'invoices' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
          )}
        >
          Invoices Module
        </button>
        <button
          onClick={() => setActiveSection('jobcards')}
          className={cn(
            "px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all cursor-pointer",
            activeSection === 'jobcards' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
          )}
        >
          Job Cards Module
        </button>
        <button
          onClick={() => setActiveSection('bills')}
          className={cn(
            "px-6 py-3 rounded-xl text-sm font-black uppercase tracking-widest transition-all cursor-pointer",
            activeSection === 'bills' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
          )}
        >
          Bills
        </button>
      </div>

      {activeSection === 'billing' ? (
        <>
          {/* Billing Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-900">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Invoiced</span>
          </div>
          <p className="text-2xl font-black text-slate-900">{formatCurrency(stats.totalInvoiced)}</p>
          <p className="text-xs text-slate-400 mt-1 font-bold">All time billing</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-600">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Pending</span>
          </div>
          <p className="text-2xl font-black text-slate-900">{formatCurrency(stats.pendingAmount)}</p>
          <p className="text-xs text-orange-500 mt-1 font-bold">{stats.pendingCount} invoices awaiting payment</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center text-red-600">
              <AlertCircle className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Overdue</span>
          </div>
          <p className="text-2xl font-black text-slate-900">{formatCurrency(stats.overdueAmount)}</p>
          <p className="text-xs text-red-500 mt-1 font-bold">{stats.overdueCount} critical overdue</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Paid</span>
          </div>
          <p className="text-2xl font-black text-slate-900">{formatCurrency(stats.paidAmount)}</p>
          <p className="text-xs text-emerald-500 mt-1 font-bold">Successfully collected</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Incoming Technical Survey Reports Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <Wrench className="w-6 h-6 text-orange-500" />
              Incoming Technical Survey Reports
              {pendingReports.filter(r => r.status === 'submitted_to_billing').length > 0 && (
                <span className="px-2.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-black rounded-full">
                  {pendingReports.filter(r => r.status === 'submitted_to_billing').length}
                </span>
              )}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {pendingReports.filter(r => r.status === 'submitted_to_billing').length > 0 ? (
              pendingReports.filter(r => r.status === 'submitted_to_billing').map((report, idx) => (
                <motion.div
                  key={`pending-sub-${report.id}-${idx}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white p-6 rounded-3xl border border-slate-100 hover:border-blue-900/20 transition-all group"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-blue-900 group-hover:text-white transition-all">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{report.clientName}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest">{report.type}</span>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                       <button
                        onClick={() => handleConvertReport(report)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-xl font-bold hover:bg-blue-800 transition-all"
                      >
                        <span>Review & Bill</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="bg-white border-2 border-dashed border-slate-100 rounded-[32px] p-8 text-center">
                <p className="text-slate-400 font-bold italic">No new survey reports for billing.</p>
              </div>
            )}
          </div>
        </div>

        {/* Reviewed Survey Reports (Awaiting Invoicing) */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-900" />
              Reviewed Survey Reports (Awaiting Invoicing)
              {pendingReports.filter(r => r.status === 'technical_survey_report').length > 0 && (
                <span className="px-2.5 py-0.5 bg-blue-100 text-blue-600 text-xs font-black rounded-full">
                  {pendingReports.filter(r => r.status === 'technical_survey_report').length}
                </span>
              )}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {pendingReports.filter(r => r.status === 'technical_survey_report').length > 0 ? (
              pendingReports.filter(r => r.status === 'technical_survey_report').map((report, idx) => (
                <motion.div
                  key={`pending-tech-${report.id}-${idx}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white p-6 rounded-3xl border border-slate-100 hover:border-blue-900/20 transition-all group"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-blue-900 group-hover:text-white transition-all">
                        <Receipt className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900">{report.clientName}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Reviewed</span>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {report.updatedAt?.toDate?.()?.toLocaleDateString() || report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => printSurveyReport(report)}
                        className="p-2 text-slate-400 hover:text-blue-900 transition-colors"
                        title="Print Survey Report"
                      >
                        <Printer className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleConvertReport(report)}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-blue-900 rounded-xl font-bold hover:bg-blue-900 hover:text-white transition-all"
                      >
                        <span>Convert to Invoice</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="bg-white border-2 border-dashed border-slate-100 rounded-[32px] p-8 text-center">
                <p className="text-slate-400 font-bold italic">No reviewed survey reports pending.</p>
              </div>
            )}
          </div>
        </div>

        {/* Permission Requests / Requisitions Section */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-emerald-600" />
              Permission Requests (Requisitions)
            </h2>
          </div>
          
          <div className="bg-white rounded-[32px] border border-slate-100 overflow-hidden shadow-sm">
            <div className="p-12 text-center border-b border-slate-50">
               <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">My Financial Permissions</h3>
                <p className="text-slate-500 max-w-sm mx-auto mb-6">Manage your requisition requests for field surveys, equipment or office supplies.</p>
                <div className="flex justify-center">
                   <button 
                    onClick={() => {
                        const event = new CustomEvent('openCashRequisition');
                        window.dispatchEvent(event);
                    }}
                    className="px-8 py-3 bg-emerald-600 text-white rounded-2xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-500/20 flex items-center gap-2"
                   >
                     <Plus className="w-5 h-5" />
                     New Request
                   </button>
                </div>
            </div>

            {requisitions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-slate-50/50">
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Code</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Site</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Requested By</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Amount</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {requisitions.slice(0, 5).map(req => (
                      <tr key={req.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 font-bold text-slate-900">{req.codeNo}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-medium">{req.siteName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 font-medium">{req.requestedBy}</td>
                        <td className="px-6 py-4 text-sm font-black text-slate-900">TZS {req.totalAmount.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                            req.status === 'approved' ? 'bg-emerald-50 text-emerald-600' : 
                            req.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                          }`}>{req.status.replace(/_/g, ' ')}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Technical Survey Report Modal */}
        <AnimatePresence>
          {isSurveyModalOpen && selectedReport && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSurveyModalOpen(false)}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-5xl bg-white rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-900">
                      <FileEdit className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-black text-slate-900 tracking-tight">Technical Survey Report</h2>
                      <p className="text-slate-500 font-medium italic">{selectedReport.clientName} - {selectedReport.branchName}</p>
                    </div>
                  </div>
                  <button onClick={() => setIsSurveyModalOpen(false)} className="p-3 hover:bg-slate-100 rounded-2xl transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="bg-slate-50 p-6 rounded-[32px] border border-slate-100">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Findings & Recommendations</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Findings</label>
                          <p className="text-sm text-slate-700 font-medium bg-white p-4 rounded-xl border border-slate-200 whitespace-pre-wrap">{selectedReport.findings}</p>
                        </div>
                        <div>
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Recommendations</label>
                          <textarea
                            value={selectedReport.recommendations}
                            onChange={(e) => setSelectedReport({ ...selectedReport, recommendations: e.target.value })}
                            className="w-full h-32 p-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-medium text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-[32px] border border-slate-100">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Labour Charges</h3>
                      <div className="relative">
                        <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                        <input
                          type="number"
                          value={selectedReport.labourCharge}
                          onChange={(e) => setSelectedReport({ ...selectedReport, labourCharge: parseInt(e.target.value) || 0 })}
                          className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-medium"
                          placeholder="Enter labour charge amount..."
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-slate-50 p-6 rounded-[32px] border border-slate-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Spare Parts & Pricing</h3>
                        <button 
                          onClick={() => setIsPartSelectorOpen(true)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-800 transition-all"
                        >
                          <Plus className="w-3 h-3" /> Add Part
                        </button>
                      </div>

                      <div className="space-y-3">
                        {selectedReport.requiredSpareParts?.map((part, index) => (
                          <div key={index} className="bg-white p-4 rounded-2xl border border-slate-200 space-y-3">
                             <div className="flex items-center justify-between">
                                <div className="flex flex-col">
                                  <p className="text-sm font-bold text-slate-700">{part.name}</p>
                                  {part.unit && <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded w-fit mt-1 uppercase">{part.unit}</span>}
                                </div>
                                <button
                                  onClick={() => removeSparePart(index)}
                                  className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Quantity</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={part.quantity}
                                  onChange={(e) => updateSparePart(index, 'quantity', parseInt(e.target.value) || 1)}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                                />
                              </div>
                              <div>
                                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 block">Unit Price</label>
                                <input
                                  type="number"
                                  value={part.price}
                                  onChange={(e) => updateSparePart(index, 'price', parseInt(e.target.value) || 0)}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 pt-6 border-t border-slate-200">
                        <div className="flex items-center justify-between text-slate-900">
                          <span className="text-sm font-black uppercase tracking-widest">Total Estimated</span>
                          <span className="text-xl font-black">
                            {formatCurrency(
                              (selectedReport.requiredSpareParts || []).reduce((sum, p) => sum + (p.quantity * (p.price || 0)), 0) + 
                              (selectedReport.labourCharge || 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleSaveSurveyReport}
                        className="w-full py-4 bg-white border-2 border-slate-100 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                      >
                        <Save className="w-4 h-4" /> Save Report
                      </button>

                      <button
                        onClick={() => handleStatusChange('technical_review')}
                        className="w-full py-4 bg-orange-500 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" /> Submit to TM (Technical Manager)
                      </button>
                      
                      <button
                        onClick={handleConvertToInvoice}
                        className="w-full py-4 bg-blue-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-800 transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2"
                      >
                        <Receipt className="w-4 h-4" /> Convert to Proforma Invoice
                      </button>

                      <button
                        onClick={() => printSurveyReport(selectedReport)}
                        className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 flex items-center justify-center gap-2"
                      >
                        <Printer className="w-4 h-4" /> Print Survey Report
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Spare Part Selection Modal */}
        <AnimatePresence>
          {isPartSelectorOpen && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsPartSelectorOpen(false)}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl p-8 flex flex-col max-h-[80vh]"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-black text-slate-900">Select Spare Part</h3>
                  <button onClick={() => setIsPartSelectorOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                <div className="relative mb-6">
                  <Wrench className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search spare parts..."
                    value={partSearchTerm}
                    onChange={(e) => setPartSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all font-bold"
                  />
                </div>

                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                  {partSearchTerm && !clientSpareParts.find(p => p.name.toLowerCase() === partSearchTerm.toLowerCase()) && (
                    <button
                      onClick={async () => {
                        addSparePart({ name: partSearchTerm, price: 0 });
                        // Save to client's spare parts list
                        if (selectedReport) {
                          try {
                            await addDoc(collection(db, 'clients', selectedReport.clientId, 'spareParts'), {
                              name: partSearchTerm,
                              price: 0,
                              createdAt: Timestamp.now()
                            });
                          } catch (e) {
                            console.error('Failed to save new spare part:', e);
                          }
                        }
                        setIsPartSelectorOpen(false);
                        setPartSearchTerm('');
                      }}
                      className="w-full flex items-center justify-between p-4 bg-blue-50 hover:bg-blue-100 rounded-2xl border border-blue-200 transition-all group mb-2"
                    >
                      <div className="text-left">
                        <p className="font-bold text-blue-900">Add "{partSearchTerm}" as new part</p>
                        <p className="text-xs text-blue-600 font-bold">Custom part</p>
                      </div>
                      <Plus className="w-5 h-5 text-blue-900" />
                    </button>
                  )}
                  {clientSpareParts
                    .filter(p => p.name.toLowerCase().includes(partSearchTerm.toLowerCase()))
                    .map(part => (
                      <button
                        key={part.id}
                        onClick={() => {
                          addSparePart(part);
                          setIsPartSelectorOpen(false);
                          setPartSearchTerm('');
                        }}
                        className="w-full flex items-center justify-between p-4 hover:bg-blue-50 rounded-2xl border border-transparent hover:border-blue-100 transition-all group"
                      >
                        <div className="text-left flex flex-col gap-0.5">
                          <p className="font-bold text-slate-900 group-hover:text-blue-900">{part.name}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-400 font-bold">{formatCurrency(part.price || 0)}</p>
                            {part.unit && <span className="text-[9px] font-black text-blue-400 uppercase bg-blue-50 px-1.5 py-0.5 rounded">{part.unit}</span>}
                          </div>
                        </div>
                        <Plus className="w-5 h-5 text-slate-300 group-hover:text-blue-900" />
                      </button>
                    ))}
                  {clientSpareParts.length === 0 && !partSearchTerm && (
                    <div className="text-center py-12">
                      <p className="text-slate-400 font-bold italic">No spare parts found for this client.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Recent Invoices */}
        <div className="space-y-6">
          <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <Receipt className="w-6 h-6 text-blue-900" />
            Recent Invoices
          </h2>
          <div className="space-y-4">
            {invoices.slice(0, 5).map((invoice, idx) => (
              <motion.div
                key={`rec-inv-${invoice.id}-${idx}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white p-5 rounded-3xl border border-slate-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-black text-blue-900 uppercase tracking-widest">#{invoice.invoiceNumber}</span>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest",
                    invoice.status === 'paid' ? "bg-emerald-50 text-emerald-600" :
                    invoice.status === 'overdue' ? "bg-red-50 text-red-600" :
                    "bg-orange-50 text-orange-600"
                  )}>
                    {invoice.status}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 truncate mb-1">{invoice.clientName}</h4>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-sm font-black text-slate-900">{formatCurrency(invoice.totalAmount, invoice.currency)}</p>
                  <Link to="/invoices" className="text-xs font-bold text-blue-900 hover:underline">View</Link>
                </div>
              </motion.div>
            ))}
            <button
              onClick={() => setActiveSection('invoices')}
              className="flex items-center justify-center gap-2 w-full py-4 bg-slate-50 text-slate-500 rounded-2xl font-bold hover:bg-slate-100 transition-all cursor-pointer"
            >
              <span>Manage Invoices Module</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </>
      ) : activeSection === 'invoices' ? (
        <div className="bg-white rounded-3xl p-2 sm:p-6 shadow-sm border border-slate-100">
          <InvoicesPage />
        </div>
      ) : activeSection === 'jobcards' ? (
        <div className="space-y-6">
          <div className="bg-white p-6 sm:p-8 rounded-[32px] border border-slate-100 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-6">
              <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 w-fit">
                <button
                  onClick={() => setActiveJobcardArticle('create')}
                  className={cn(
                    "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
                    activeJobcardArticle === 'create' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
                  )}
                >
                  Create Job Card
                </button>
                <button
                  onClick={() => setActiveJobcardArticle('requests')}
                  className={cn(
                    "px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
                    activeJobcardArticle === 'requests' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
                  )}
                >
                  Requests
                  {jobCardRequests.filter(r => r.status === 'pending').length > 0 && (
                    <span className="bg-orange-500 text-white text-[9px] px-2 py-0.5 rounded-full font-black animate-pulse">
                      {jobCardRequests.filter(r => r.status === 'pending').length}
                    </span>
                  )}
                </button>
              </div>

              {activeJobcardArticle === 'create' && (
                <button
                  onClick={handleOpenNewJobCard}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all text-sm shadow-lg shadow-blue-900/10 cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Job Card</span>
                </button>
              )}
            </div>

            {activeJobcardArticle === 'create' ? (
              <div className="space-y-6">
                <div className="overflow-x-auto rounded-3xl border border-slate-100">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Job Card No</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Client Name</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Proforma No</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Proforma Amount</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Created At</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {jobCards.map((card, idx) => (
                        <tr key={`jc-${card.id}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 font-mono font-bold text-sm text-blue-900">
                            {card.jobCardNo}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-slate-900">
                            {card.clientName}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-600">
                            {card.proformaNo || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm font-black text-emerald-600">
                            {card.proformaAmount ? formatCurrency(card.proformaAmount) : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-500">
                            {card.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-right space-x-2">
                            <button
                              onClick={() => handleEditJobCard(card)}
                              className="text-xs font-bold text-blue-900 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteJobCard(card.id)}
                              className="text-xs font-bold text-red-600 hover:text-red-800 bg-red-50 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {jobCards.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-16">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
                              <FileText className="w-8 h-8" />
                            </div>
                            <p className="text-slate-500 font-bold text-sm">No Job Cards created yet.</p>
                            <p className="text-slate-400 text-xs mt-1">Click "Create Job Card" to build one manually.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="overflow-x-auto rounded-3xl border border-slate-100">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Client & Branch</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Requested By</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Date Requested</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider">Linked Job Card No</th>
                        <th className="px-6 py-4.5 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {jobCardRequests.map((req, idx) => (
                        <tr key={`jcr-${req.id}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4">
                            <span className="text-sm font-bold text-slate-900 block">{req.clientName}</span>
                            <span className="text-xs text-slate-400 font-bold">{req.branchName}</span>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-600">
                            {req.requestedBy}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-500">
                            {req.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={cn(
                              "px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-xl border",
                              req.status === 'completed' 
                                ? "bg-emerald-50 text-emerald-600 border-emerald-100" 
                                : req.status === 'under_review'
                                  ? "bg-blue-50 text-blue-600 border-blue-100"
                                  : "bg-orange-50 text-orange-600 border-orange-100"
                            )}>
                              {req.status === 'under_review' ? 'under review' : req.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-mono text-sm font-bold text-slate-700">
                            {req.jobCardNo || '-'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end items-center gap-2">
                              {req.status === 'pending' && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await updateDoc(doc(db, 'job_card_requests', req.id), {
                                        status: 'under_review',
                                        updatedAt: Timestamp.now()
                                      });
                                      toast.success('Request marked as Under Review!');
                                    } catch (error) {
                                      console.error('Error starting review:', error);
                                      toast.error('Failed to update status');
                                    }
                                  }}
                                  className="text-xs font-bold text-amber-700 hover:text-amber-800 bg-amber-50 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                                >
                                  Start Review
                                </button>
                              )}
                              {(req.status === 'pending' || req.status === 'under_review') ? (
                                <button
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setSelectedJcNoToLink('');
                                    setIsLinkModalOpen(true);
                                  }}
                                  className="text-xs font-bold text-blue-900 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-xl transition-all cursor-pointer"
                                >
                                  Edit & Select Jobcard
                                </button>
                              ) : (
                                <span className="text-xs text-slate-400 font-bold">Details Sent</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {jobCardRequests.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-16">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
                              <ClipboardList className="w-8 h-8" />
                            </div>
                            <p className="text-slate-500 font-bold text-sm">No incoming Job Card requests.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ==================== BILLS MODULE ==================== */
        <div className="space-y-6">
          <div className="bg-white p-6 sm:p-8 rounded-[32px] border border-slate-100 shadow-sm space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-6">
              <div>
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Bills & Survey Reports</h2>
                <p className="text-xs text-slate-500 font-medium mt-1">
                  Manage incoming survey reports from Technical, process bills, and review Customer Care reports after service
                </p>
              </div>

              {/* Sub-Tabs: Incoming | Report After Service | Billed */}
              <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 w-fit overflow-x-auto max-w-full">
                <button
                  onClick={() => setBillsSubTab('incoming')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap",
                    billsSubTab === 'incoming' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
                  )}
                >
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span>Incoming ({forwardedSurveys.length})</span>
                </button>
                <button
                  onClick={() => setBillsSubTab('report_after_service')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap",
                    billsSubTab === 'report_after_service' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
                  )}
                >
                  <ClipboardList className="w-4 h-4 text-emerald-600" />
                  <span>Report After Service ({customerCareReports.length})</span>
                </button>
                <button
                  onClick={() => setBillsSubTab('billed')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap",
                    billsSubTab === 'billed' ? "bg-white text-blue-900 shadow-sm" : "text-slate-500 hover:text-blue-900"
                  )}
                >
                  <CheckCircle2 className="w-4 h-4 text-purple-600" />
                  <span>Billed ({billedItems.length})</span>
                </button>
              </div>
            </div>

            {/* Search Filter */}
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-4 top-3.5 text-slate-400" />
              <input
                type="text"
                value={billsSearchTerm}
                onChange={e => setBillsSearchTerm(e.target.value)}
                placeholder="Search reports, sites, technicians, or proformas..."
                className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-900/10"
              />
            </div>

            {/* Feature 1: INCOMING */}
            {billsSubTab === 'incoming' && (
              <div className="space-y-4">
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-extrabold uppercase tracking-wider border-b border-slate-100">
                        <th className="py-4 px-5">Site / Client Name</th>
                        <th className="py-4 px-5">Machine Type</th>
                        <th className="py-4 px-5">Technician</th>
                        <th className="py-4 px-5">Client Request / Scope</th>
                        <th className="py-4 px-5">Checking Result</th>
                        <th className="py-4 px-5">Date</th>
                        <th className="py-4 px-5">Status</th>
                        <th className="py-4 px-5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {forwardedSurveys
                        .filter((s: any) => {
                          const term = billsSearchTerm.toLowerCase();
                          return !term || 
                            (s.siteName && s.siteName.toLowerCase().includes(term)) ||
                            (s.clientName && s.clientName.toLowerCase().includes(term)) ||
                            (s.technicianName && s.technicianName.toLowerCase().includes(term)) ||
                            (s.clientRequest && s.clientRequest.toLowerCase().includes(term));
                        })
                        .map((survey: any, idx: number) => (
                          <tr key={`fwd-srv-${survey.id}-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-4 px-5 font-black text-slate-900">{survey.siteName || survey.clientName || 'General Site'}</td>
                            <td className="py-4 px-5 font-semibold text-slate-700">{survey.machineType || 'Air Conditioner'}</td>
                            <td className="py-4 px-5 text-slate-600 font-medium">{survey.technicianName || 'Field Tech'}</td>
                            <td className="py-4 px-5 text-slate-600 max-w-xs truncate">{survey.clientRequest || survey.findings || 'Site Inspection'}</td>
                            <td className="py-4 px-5 text-slate-600 max-w-xs truncate">{survey.checkingResult || survey.recommendations || 'Checked'}</td>
                            <td className="py-4 px-5 text-slate-500">{survey.createdAt || 'Recent'}</td>
                            <td className="py-4 px-5">
                              <span className={cn(
                                "px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider",
                                survey.status === 'Billed' ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                              )}>
                                {survey.status || 'Forwarded'}
                              </span>
                            </td>
                            <td className="py-4 px-5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setViewingSurveyReport(survey)}
                                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-xl font-bold text-xs shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                                  title="View Technical Survey Report with Letterhead"
                                >
                                  <Eye className="w-3.5 h-3.5 text-slate-600" />
                                  <span>View</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedIncomingReport(survey);
                                    setIsCreateBillModalOpen(true);
                                  }}
                                  className="px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs shadow-sm transition-all cursor-pointer flex items-center gap-1.5"
                                >
                                  <Receipt className="w-3.5 h-3.5" />
                                  <span>Bill</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      {forwardedSurveys.length === 0 && (
                        <tr>
                          <td colSpan={8} className="py-12 text-center text-slate-400 font-bold italic">
                            No forwarded technical survey reports in Incoming yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Feature 2: REPORT AFTER SERVICE */}
            {billsSubTab === 'report_after_service' && (
              <div className="space-y-4">
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-extrabold uppercase tracking-wider border-b border-slate-100">
                        <th className="py-4 px-5">Client Name</th>
                        <th className="py-4 px-5">Branch</th>
                        <th className="py-4 px-5">Department</th>
                        <th className="py-4 px-5">Task Details</th>
                        <th className="py-4 px-5">Findings & Comments</th>
                        <th className="py-4 px-5">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {customerCareReports
                        .filter((report: any) => {
                          const term = billsSearchTerm.toLowerCase();
                          return !term || 
                            (report.clientName && report.clientName.toLowerCase().includes(term)) ||
                            (report.task && report.task.toLowerCase().includes(term)) ||
                            (report.comment && report.comment.toLowerCase().includes(term));
                        })
                        .map((ccReport: any, idx: number) => (
                          <tr key={`cc-rep-${ccReport.id}-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-4 px-5 font-black text-slate-900">{ccReport.clientName}</td>
                            <td className="py-4 px-5 font-semibold text-slate-700">{ccReport.branch || 'Main Branch'}</td>
                            <td className="py-4 px-5 text-slate-600">{ccReport.userDepartment || 'Customer Care'}</td>
                            <td className="py-4 px-5 text-slate-800 max-w-xs font-medium truncate">{ccReport.task || 'Service Follow-up'}</td>
                            <td className="py-4 px-5 text-slate-600 max-w-xs truncate">{ccReport.comment || ccReport.findings || 'Satisfactory'}</td>
                            <td className="py-4 px-5">
                              <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                {ccReport.status || 'Completed'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      {customerCareReports.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-slate-400 font-bold italic">
                            No Customer Care service reports found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Feature 3: BILLED */}
            {billsSubTab === 'billed' && (
              <div className="space-y-4">
                <div className="overflow-x-auto rounded-2xl border border-slate-100">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 font-extrabold uppercase tracking-wider border-b border-slate-100">
                        <th className="py-4 px-5">Site / Client Name</th>
                        <th className="py-4 px-5">Proforma Invoice No</th>
                        <th className="py-4 px-5">Bill Amount</th>
                        <th className="py-4 px-5">Billed Date</th>
                        <th className="py-4 px-5">Billed By</th>
                        <th className="py-4 px-5">Status</th>
                        <th className="py-4 px-5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {billedItems
                        .filter((b: any) => {
                          const term = billsSearchTerm.toLowerCase();
                          return !term || 
                            (b.siteName && b.siteName.toLowerCase().includes(term)) ||
                            (b.proformaNo && String(b.proformaNo).toLowerCase().includes(term)) ||
                            (b.billedBy && b.billedBy.toLowerCase().includes(term));
                        })
                        .map((b: any, idx: number) => (
                          <tr key={`billed-${b.id}-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                            <td className="py-4 px-5 font-black text-slate-900">{b.siteName || b.clientName}</td>
                            <td className="py-4 px-5 font-mono font-bold text-blue-900">#{b.proformaNo}</td>
                            <td className="py-4 px-5 font-black text-emerald-700">{formatCurrency(b.billAmount)}</td>
                            <td className="py-4 px-5 text-slate-600">{b.billDate}</td>
                            <td className="py-4 px-5 text-slate-600 font-medium">{b.billedBy}</td>
                            <td className="py-4 px-5">
                              <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-800">
                                Billed
                              </span>
                            </td>
                            <td className="py-4 px-5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setViewingBilledInvoice(b)}
                                  className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-900 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                                  title="Preview Proforma Invoice"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  <span>Invoice</span>
                                </button>
                                <button
                                  onClick={() => handleUndoBilled(b)}
                                  className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                                  title="Undo Billed Status and Return to Incoming"
                                >
                                  <Undo2 className="w-3.5 h-3.5" />
                                  <span>Undo</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      {billedItems.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-12 text-center text-slate-400 font-bold italic">
                            No billed proformas yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create / Edit Job Card Modal */}
      <AnimatePresence>
        {isJobCardModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-100"
            >
              <button
                type="button"
                onClick={() => setIsJobCardModalOpen(false)}
                className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="mb-6">
                <h3 className="text-xl font-black text-slate-900">
                  {editingJobCardId ? 'Edit Job Card' : 'Create Job Card'}
                </h3>
                <p className="text-sm text-slate-500 font-medium">Please fill in the Job Card information across the 3 steps.</p>
                
                {/* Steps indicator */}
                <div className="flex gap-2 mt-4">
                  {[1, 2, 3].map((step) => (
                    <div
                      key={step}
                      className={cn(
                        "h-1.5 flex-1 rounded-full transition-all",
                        jobCardFormStep >= step ? "bg-blue-900" : "bg-slate-100"
                      )}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-[10px] font-black uppercase tracking-wider text-slate-400 mt-2">
                  <span className={cn(jobCardFormStep === 1 && "text-blue-900")}>Step 1: General Info</span>
                  <span className={cn(jobCardFormStep === 2 && "text-blue-900")}>Step 2: Terms & Billing</span>
                  <span className={cn(jobCardFormStep === 3 && "text-blue-900")}>Step 3: Tech Details</span>
                </div>
              </div>

              <form onSubmit={handleSaveJobCard} className="space-y-6">
                {jobCardFormStep === 1 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Client *</label>
                      <select
                        required
                        value={jcClientId}
                        onChange={(e) => setJcClientId(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold text-slate-700"
                      >
                        <option value="">Select a client...</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">LPO Number</label>
                      <input
                        type="text"
                        value={jcLpoNo}
                        onChange={(e) => setJcLpoNo(e.target.value)}
                        placeholder="Enter LPO No..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">LPO Date</label>
                      <input
                        type="date"
                        value={jcLpoDate}
                        onChange={(e) => setJcLpoDate(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Proforma Invoice Number</label>
                      <input
                        type="text"
                        value={jcProformaNo}
                        onChange={(e) => setJcProformaNo(e.target.value)}
                        placeholder="Proforma No..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Proforma Date</label>
                      <input
                        type="date"
                        value={jcProformaDate}
                        onChange={(e) => setJcProformaDate(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Proforma Amount (TZS)</label>
                      <input
                        type="number"
                        value={jcProformaAmount || ''}
                        onChange={(e) => setJcProformaAmount(Number(e.target.value))}
                        placeholder="Enter proforma amount..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Customer Request / Description of Job *</label>
                      <textarea
                        required
                        value={jcCustomerRequest}
                        onChange={(e) => setJcCustomerRequest(e.target.value)}
                        placeholder="Write customer request / scope..."
                        rows={3}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-medium"
                      />
                    </div>
                  </div>
                )}

                {jobCardFormStep === 2 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Service Memo Number</label>
                      <input
                        type="text"
                        value={jcServiceMemoNo}
                        onChange={(e) => setJcServiceMemoNo(e.target.value)}
                        placeholder="Service Memo No..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Service Memo Date</label>
                      <input
                        type="date"
                        value={jcServiceMemoDate}
                        onChange={(e) => setJcServiceMemoDate(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tax Invoice Number</label>
                      <input
                        type="text"
                        value={jcInvoiceNo}
                        onChange={(e) => setJcInvoiceNo(e.target.value)}
                        placeholder="Tax Invoice No..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tax Invoice Amount</label>
                      <input
                        type="number"
                        value={jcInvoiceAmount || ''}
                        onChange={(e) => setJcInvoiceAmount(Number(e.target.value))}
                        placeholder="Tax Invoice Amount..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tax Invoice Date</label>
                      <input
                        type="date"
                        value={jcInvoiceDate}
                        onChange={(e) => setJcInvoiceDate(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Job Terms *</label>
                      <select
                        required
                        value={jcJobTerms}
                        onChange={(e) => setJcJobTerms(e.target.value as any)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold text-slate-700"
                      >
                        <option value="Contract">Contract</option>
                        <option value="LPO">LPO</option>
                        <option value="Quotation">Quotation</option>
                        <option value="Verbal">Verbal</option>
                        <option value="Email">Email</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Quotation Reference</label>
                      <input
                        type="text"
                        value={jcQuotationRef}
                        onChange={(e) => setJcQuotationRef(e.target.value)}
                        placeholder="Quotation ref..."
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Quotation Date</label>
                      <input
                        type="date"
                        value={jcQuotationDate}
                        onChange={(e) => setJcQuotationDate(e.target.value)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Is For Service? *</label>
                      <select
                        required
                        value={jcIsForService}
                        onChange={(e) => setJcIsForService(e.target.value as any)}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold text-slate-700"
                      >
                        <option value="True">True</option>
                        <option value="False">False</option>
                      </select>
                    </div>
                  </div>
                )}

                {jobCardFormStep === 3 && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Technical Survey Report Findings</label>
                      <textarea
                        value={jcTechnicalReport}
                        onChange={(e) => setJcTechnicalReport(e.target.value)}
                        placeholder="Describe findings / inspection report details..."
                        rows={4}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-medium"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Workdone Details</label>
                      <textarea
                        value={jcWorkdoneDetails}
                        onChange={(e) => setJcWorkdoneDetails(e.target.value)}
                        placeholder="Enter repair / maintenance services performed..."
                        rows={4}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-medium"
                      />
                    </div>
                  </div>
                )}

                {/* Footer buttons */}
                <div className="flex gap-4 pt-6 border-t border-slate-100">
                  {jobCardFormStep > 1 && (
                    <button
                      type="button"
                      onClick={() => setJobCardFormStep(prev => prev - 1)}
                      className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl transition-all cursor-pointer"
                    >
                      Back
                    </button>
                  )}
                  
                  <div className="flex-1" />

                  <button
                    type="button"
                    onClick={() => setIsJobCardModalOpen(false)}
                    className="px-6 py-3 bg-slate-50 hover:bg-slate-100 text-slate-500 font-bold rounded-2xl transition-all cursor-pointer"
                  >
                    Cancel
                  </button>

                  {jobCardFormStep < 3 ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (jobCardFormStep === 1) {
                          if (!jcClientId) {
                            toast.error('Client is required');
                            return;
                          }
                          if (!jcCustomerRequest.trim()) {
                            toast.error('Customer Request / Job Description is required');
                            return;
                          }
                        }
                        setJobCardFormStep(prev => prev + 1);
                      }}
                      className="px-6 py-3 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-2xl transition-all cursor-pointer shadow-lg shadow-blue-900/10"
                    >
                      Next Step
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl transition-all cursor-pointer shadow-lg shadow-emerald-600/10"
                    >
                      {editingJobCardId ? 'Update Job Card' : 'Save Job Card'}
                    </button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Link Job Card Request Modal */}
      <AnimatePresence>
        {isLinkModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-8 max-w-lg w-full relative shadow-2xl border border-slate-100"
            >
              <button
                type="button"
                onClick={() => {
                  setIsLinkModalOpen(false);
                  setSelectedRequest(null);
                }}
                className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="mb-6">
                <h3 className="text-xl font-black text-slate-900">Link Job Card to Request</h3>
                <p className="text-sm text-slate-500 font-medium">Select a Job Card from your list of Created Job Cards to link with this request.</p>
                <div className="mt-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-100 text-xs">
                  <p className="font-bold text-slate-800">Requesting client: {selectedRequest.clientName}</p>
                  <p className="text-slate-500 font-medium mt-0.5">Requested by: {selectedRequest.requestedBy}</p>
                </div>
              </div>

              <form onSubmit={handleLinkJobCard} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Created Job Card *</label>
                  <select
                    required
                    value={selectedJcNoToLink}
                    onChange={(e) => setSelectedJcNoToLink(e.target.value)}
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-900/10 font-bold text-slate-700"
                  >
                    <option value="">Select a job card...</option>
                    {jobCards
                      .filter(card => card.clientName === selectedRequest.clientName)
                      .map(card => (
                        <option key={card.id} value={card.jobCardNo}>
                          Job Card: {card.jobCardNo} - {card.proformaNo ? `Proforma: ${card.proformaNo}` : 'No Proforma'}
                        </option>
                      ))}
                    {jobCards.filter(card => card.clientName === selectedRequest.clientName).length === 0 && (
                      <option disabled value="">No Job Cards found for this client</option>
                    )}
                  </select>
                  {jobCards.filter(card => card.clientName === selectedRequest.clientName).length === 0 && (
                    <p className="text-xs text-red-500 font-medium mt-1">Please create a Job Card for this client first under the "Create Job Card" tab.</p>
                  )}
                </div>

                <div className="flex gap-4 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLinkModalOpen(false);
                      setSelectedRequest(null);
                    }}
                    className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingLink || !selectedJcNoToLink}
                    className="flex-1 px-6 py-3 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-2xl transition-all cursor-pointer shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
                  >
                    {isSubmittingLink ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Link & Submit</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Create Bill Modal */}
      <AnimatePresence>
        {isCreateBillModalOpen && selectedIncomingReport && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-100"
            >
              <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
                <div>
                  <h3 className="text-xl font-black text-slate-900 flex items-center gap-2">
                    <Receipt className="w-6 h-6 text-blue-900" />
                    <span>Create Bill</span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    Link proforma invoice and process bill for technical survey report
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateBillModalOpen(false);
                    setSelectedIncomingReport(null);
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Selected Report Snapshot */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 mb-6 space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="font-black text-blue-900 text-sm">
                    {selectedIncomingReport.siteName || selectedIncomingReport.clientName || 'Site Survey'}
                  </span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-bold rounded text-[10px] uppercase">
                    Technical Survey Report
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-600 font-medium">
                  <div><span className="text-slate-400 uppercase font-bold text-[10px] block">Machine Type:</span> {selectedIncomingReport.machineType || 'AC Unit'}</div>
                  <div><span className="text-slate-400 uppercase font-bold text-[10px] block">Technician:</span> {selectedIncomingReport.technicianName || 'Tech'}</div>
                </div>
                <div>
                  <span className="text-slate-400 uppercase font-bold text-[10px] block">Checking Results:</span>
                  <p className="text-slate-700">{selectedIncomingReport.checkingResult || selectedIncomingReport.findings || 'Inspected'}</p>
                </div>
              </div>

              <form onSubmit={handleCreateBillSubmit} className="space-y-5 text-xs font-medium">
                {/* Manual Proforma Toggle */}
                <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="manualProformaToggle"
                      checked={isManualProforma}
                      onChange={e => setIsManualProforma(e.target.checked)}
                      className="w-4 h-4 text-blue-900 rounded focus:ring-blue-900 cursor-pointer"
                    />
                    <label htmlFor="manualProformaToggle" className="font-bold text-slate-800 cursor-pointer">
                      Proforma Invoice not in system? (Manual input & upload)
                    </label>
                  </div>
                </div>

                {/* Proforma Selection or Manual Input */}
                {!isManualProforma ? (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Select Proforma Invoice Number(s) <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Search proforma by number or client..."
                        value={proformaSearch}
                        onChange={e => setProformaSearch(e.target.value)}
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-900/10"
                      />
                      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1 bg-slate-50/50">
                        {invoices
                          .filter(inv => inv.type === 'proforma' || inv.invoiceNumber)
                          .filter(inv => !proformaSearch || inv.invoiceNumber.toLowerCase().includes(proformaSearch.toLowerCase()) || inv.clientName.toLowerCase().includes(proformaSearch.toLowerCase()))
                          .map(inv => {
                            const isSelected = selectedProformaIds.includes(inv.id);
                            return (
                              <div
                                key={inv.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedProformaIds(selectedProformaIds.filter(id => id !== inv.id));
                                  } else {
                                    setSelectedProformaIds([...selectedProformaIds, inv.id]);
                                  }
                                }}
                                className={cn(
                                  "p-2.5 rounded-lg flex items-center justify-between cursor-pointer transition-all border text-xs",
                                  isSelected ? "bg-blue-900 text-white border-blue-900 font-bold" : "bg-white hover:bg-slate-100 text-slate-800 border-slate-200"
                                )}
                              >
                                <div>
                                  <span className="font-bold">#{inv.invoiceNumber}</span>
                                  <span className="ml-2 opacity-80 text-[11px]">{inv.clientName}</span>
                                </div>
                                <span>{formatCurrency(inv.totalAmount)}</span>
                              </div>
                            );
                          })}
                        {invoices.filter(inv => inv.type === 'proforma' || inv.invoiceNumber).length === 0 && (
                          <div className="p-3 text-center text-slate-400 italic">No proforma invoices found in system. Use manual input toggle above.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                        Proforma Invoice Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required={isManualProforma}
                        value={manualProformaNo}
                        onChange={e => setManualProformaNo(e.target.value)}
                        placeholder="e.g. PROF-2026-089"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-900/10"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                        Upload Proforma Invoice File (Optional)
                      </label>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handleFileUpload}
                        className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-blue-900 hover:file:bg-blue-100 cursor-pointer"
                      />
                    </div>
                  </div>
                )}

                {/* Bill Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Bill Amount (TZS) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      value={billAmount}
                      onChange={e => setBillAmount(Number(e.target.value))}
                      placeholder="0"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-black text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/10"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      Bill Date
                    </label>
                    <input
                      type="date"
                      value={billDate}
                      onChange={e => setBillDate(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-900/10"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Remarks / Notes
                  </label>
                  <textarea
                    rows={2}
                    value={billRemarks}
                    onChange={e => setBillRemarks(e.target.value)}
                    placeholder="Enter any additional billing notes..."
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-900/10 resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreateBillModalOpen(false);
                      setSelectedIncomingReport(null);
                    }}
                    className="px-5 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingBill}
                    className="px-6 py-2.5 bg-blue-900 hover:bg-blue-800 text-white font-bold rounded-xl shadow-lg shadow-blue-900/20 transition-all cursor-pointer flex items-center gap-2"
                  >
                    {isSubmittingBill ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                    <span>Submit Bill & Return to Technical</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Technical Survey Report Preview Modal with Letterhead */}
      <AnimatePresence>
        {viewingSurveyReport && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-6 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-100 my-8"
            >
              {/* Action Header */}
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-600 animate-pulse" />
                  <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">
                    Technical Survey Report Preview
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadSurveyReportPDF(viewingSurveyReport)}
                    className="px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Print</span>
                  </button>
                  <button
                    onClick={() => setViewingSurveyReport(null)}
                    className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-all cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Printable Report Document Sheet */}
              <div className="bg-white p-6 sm:p-10 rounded-2xl border border-slate-200 shadow-sm space-y-6 text-slate-800" id="printable-survey-report">
                {/* Company Letterhead Header */}
                {letterheadUrl ? (
                  <div className="w-full flex justify-center pb-4 border-b border-slate-200">
                    <img src={letterheadUrl} alt="Company Letterhead" className="max-h-32 object-contain w-full" referrerPolicy="no-referrer" />
                  </div>
                ) : (
                  <div className="text-center pb-6 border-b-2 border-blue-900">
                    <h1 className="text-2xl font-black text-blue-900 tracking-wider uppercase">
                      BRIGHT TECHNICAL SYSTEMS & GENERAL SUPPLIES LIMITED
                    </h1>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mt-1">
                      Air Conditioning, Refrigeration & Electrical Engineering Specialists
                    </p>
                  </div>
                )}

                {/* Document Title Banner */}
                <div className="bg-slate-900 text-white p-3 rounded-xl text-center uppercase tracking-widest font-black text-sm">
                  Technical Survey Report
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs">
                  <div>
                    <span className="text-slate-400 font-bold block uppercase text-[10px]">Site / Client Name</span>
                    <span className="font-extrabold text-slate-900 uppercase">{viewingSurveyReport.siteName || viewingSurveyReport.clientName || 'General Site'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-bold block uppercase text-[10px]">Equipment Type</span>
                    <span className="font-bold text-slate-800">{viewingSurveyReport.machineType || 'Air Conditioner'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-bold block uppercase text-[10px]">Field Technician</span>
                    <span className="font-bold text-slate-800">{viewingSurveyReport.technicianName || 'Field Tech'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-bold block uppercase text-[10px]">Survey Date</span>
                    <span className="font-bold text-slate-800">{viewingSurveyReport.createdAt || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 font-bold block uppercase text-[10px]">Report Status</span>
                    <span className="font-black text-blue-800 uppercase">{viewingSurveyReport.status || 'Forwarded'}</span>
                  </div>
                </div>

                {/* Report Content Sections */}
                <div className="space-y-4 text-xs">
                  <div>
                    <h4 className="font-extrabold text-blue-900 uppercase tracking-wider mb-1">1. Client Request / Problem Description</h4>
                    <p className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-slate-800 font-medium whitespace-pre-wrap leading-relaxed">
                      {viewingSurveyReport.clientRequest || viewingSurveyReport.title || viewingSurveyReport.findings || 'No specific client request details recorded.'}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-extrabold text-blue-900 uppercase tracking-wider mb-1">2. Checking Result / Inspection Findings</h4>
                    <p className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-slate-800 font-medium whitespace-pre-wrap leading-relaxed">
                      {viewingSurveyReport.checkingResult || viewingSurveyReport.findings || 'Inspection completed successfully.'}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-extrabold text-blue-900 uppercase tracking-wider mb-1">3. Remedial Recommendations</h4>
                    <p className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-slate-800 font-medium whitespace-pre-wrap leading-relaxed">
                      {viewingSurveyReport.remedialRecommendation || viewingSurveyReport.recommendations || 'Proceed with standard maintenance and part replacement as needed.'}
                    </p>
                  </div>

                  {viewingSurveyReport.requiredSpareParts && viewingSurveyReport.requiredSpareParts.length > 0 && (
                    <div>
                      <h4 className="font-extrabold text-blue-900 uppercase tracking-wider mb-2">4. Required Spare Parts</h4>
                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-slate-100 text-slate-700 font-bold">
                              <th className="p-2.5">Part Name</th>
                              <th className="p-2.5 text-center">Qty</th>
                              <th className="p-2.5 text-right">Unit Price</th>
                              <th className="p-2.5 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {viewingSurveyReport.requiredSpareParts.map((part: any, idx: number) => (
                              <tr key={idx}>
                                <td className="p-2.5 font-semibold text-slate-800">{part.name}</td>
                                <td className="p-2.5 text-center font-bold">{part.quantity}</td>
                                <td className="p-2.5 text-right">{formatCurrency(part.price || 0)}</td>
                                <td className="p-2.5 text-right font-bold text-slate-900">{formatCurrency((part.quantity || 1) * (part.price || 0))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>

                {/* Signatures Footer */}
                <div className="pt-8 mt-8 border-t border-slate-200 grid grid-cols-2 gap-8 text-xs">
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Prepared By (Technician):</p>
                    <div className="mt-6 border-b border-slate-400 w-48" />
                    <p className="font-bold text-slate-800 mt-1">{viewingSurveyReport.technicianName || 'Field Technician'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[10px]">Reviewed By (Billing / Technical):</p>
                    <div className="mt-6 border-b border-slate-400 w-48" />
                    <p className="font-bold text-slate-800 mt-1">{profile?.displayName || 'Billing Department'}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Proforma Invoice Preview Modal for Billed Reports */}
      <AnimatePresence>
        {viewingBilledInvoice && (() => {
          const matchedInvoice = invoices.find(inv => 
            (inv.invoiceNumber && viewingBilledInvoice.proformaNo && String(inv.invoiceNumber).toLowerCase().trim() === String(viewingBilledInvoice.proformaNo).toLowerCase().trim()) ||
            (inv.id && viewingBilledInvoice.proformaNo && inv.id === viewingBilledInvoice.proformaNo) ||
            (viewingBilledInvoice.proformaNo && inv.invoiceNumber && String(viewingBilledInvoice.proformaNo).toLowerCase().includes(inv.invoiceNumber.toLowerCase())) ||
            (viewingBilledInvoice.reportId && inv.reportId === viewingBilledInvoice.reportId)
          );

          const attachedUrl = viewingBilledInvoice.attachedInvoiceUrl || (matchedInvoice as any)?.attachedInvoiceUrl || (matchedInvoice as any)?.invoiceUrl || '';
          const hasAttachedFile = Boolean(attachedUrl);

          const itemsList = matchedInvoice && matchedInvoice.items && matchedInvoice.items.length > 0
            ? matchedInvoice.items
            : [
                {
                  serialNumber: '1',
                  description: viewingBilledInvoice.clientRequest || 'Technical Survey & Maintenance Service Billing',
                  quantity: 1,
                  unit: 'Job',
                  unitPrice: viewingBilledInvoice.billAmount || 0,
                  amount: viewingBilledInvoice.billAmount || 0
                }
              ];

          const totalAmount = matchedInvoice?.totalAmount || viewingBilledInvoice.billAmount || 0;

          return (
            <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-md overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-100 rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col relative shadow-2xl border border-slate-200 my-auto"
              >
                {/* Action Header */}
                <div className="p-4 sm:p-6 border-b border-slate-200 bg-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shrink-0 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-2xl flex items-center justify-center text-purple-900 shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                          Proforma Invoice Preview
                        </h2>
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-black bg-purple-100 text-purple-900 border border-purple-200">
                          #{viewingBilledInvoice.proformaNo}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-medium">
                        {viewingBilledInvoice.siteName || viewingBilledInvoice.clientName} • Billed Date: {viewingBilledInvoice.billDate || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Controls: Tabs & Buttons */}
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto justify-end">
                    {hasAttachedFile && (
                      <div className="flex items-center p-1 bg-slate-100 rounded-2xl border border-slate-200">
                        <button
                          onClick={() => setProformaModalTab('attached')}
                          className={cn(
                            "px-3 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer",
                            proformaModalTab === 'attached'
                              ? "bg-purple-900 text-white shadow-sm"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/50"
                          )}
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                          <span>Attached Document</span>
                        </button>
                        <button
                          onClick={() => setProformaModalTab('document')}
                          className={cn(
                            "px-3 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer",
                            proformaModalTab === 'document'
                              ? "bg-purple-900 text-white shadow-sm"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/50"
                          )}
                        >
                          <Printer className="w-3.5 h-3.5" />
                          <span>Print Sheet</span>
                        </button>
                      </div>
                    )}

                    {hasAttachedFile && proformaModalTab === 'attached' && (
                      <a
                        href={attachedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3.5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
                        title="Open attached file in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="hidden sm:inline">Open File</span>
                      </a>
                    )}

                    <button
                      onClick={() => downloadProformaInvoicePDF(viewingBilledInvoice)}
                      className="px-4 py-2 bg-blue-900 hover:bg-blue-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download PDF</span>
                    </button>

                    <button
                      onClick={() => {
                        if (hasAttachedFile && proformaModalTab === 'attached') {
                          const printWin = window.open(attachedUrl, '_blank');
                          if (printWin) printWin.focus();
                        } else {
                          window.print();
                        }
                      }}
                      className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Print</span>
                    </button>

                    <button
                      onClick={() => setViewingBilledInvoice(null)}
                      className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-all cursor-pointer ml-1"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Modal Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6">
                  {hasAttachedFile && proformaModalTab === 'attached' ? (
                    <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-md space-y-4">
                      <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Paperclip className="w-5 h-5 text-purple-900" />
                          <div>
                            <p className="text-xs font-black text-purple-950">Real Attached Proforma Invoice</p>
                            <p className="text-[11px] text-purple-700">Previewing the actual document file attached during billing</p>
                          </div>
                        </div>
                        <a
                          href={attachedUrl}
                          download={`Proforma_Invoice_${viewingBilledInvoice.proformaNo}`}
                          className="px-3 py-1.5 bg-purple-900 text-white text-xs font-bold rounded-lg hover:bg-purple-800 transition-all flex items-center gap-1"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Download Attachment</span>
                        </a>
                      </div>

                      {/* Embedded File Viewer */}
                      <div className="w-full bg-slate-100 rounded-xl overflow-hidden border border-slate-200 min-h-[600px] flex items-center justify-center">
                        {attachedUrl.startsWith('data:image') || attachedUrl.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i) ? (
                          <img
                            src={attachedUrl}
                            alt={`Proforma Invoice #${viewingBilledInvoice.proformaNo}`}
                            className="max-h-[650px] w-auto max-w-full object-contain rounded-xl shadow-lg"
                          />
                        ) : (
                          <iframe
                            src={attachedUrl}
                            className="w-full h-[650px] border-0 rounded-xl shadow-inner bg-white"
                            title={`Proforma Invoice #${viewingBilledInvoice.proformaNo}`}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Official Printable Proforma Invoice Sheet (Invoices Module Parity) */
                    <div 
                      id="printable-proforma-sheet"
                      className="bg-white p-6 sm:p-12 rounded-2xl border border-slate-200 shadow-xl max-w-[210mm] mx-auto relative overflow-hidden font-sans text-slate-800 space-y-8"
                    >
                      {/* Watermark for Proforma */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03] rotate-[-45deg] select-none">
                        <span className="text-[140px] font-black text-slate-900">PROFORMA</span>
                      </div>

                      {/* Company Letterhead Header */}
                      {letterheadUrl ? (
                        <div className="w-full flex justify-center pb-4 border-b-2 border-blue-900">
                          <img src={letterheadUrl} alt="Company Letterhead" className="max-h-28 object-contain w-full" referrerPolicy="no-referrer" />
                        </div>
                      ) : (
                        <div className="flex justify-between items-start border-b-4 border-blue-900 pb-6 relative z-10">
                          <div className="space-y-2">
                            <h1 className="text-2xl font-black text-blue-950 tracking-tight">
                              BRIGHT TECHNICAL SYSTEMS
                            </h1>
                            <p className="text-xs font-bold text-orange-600 tracking-widest uppercase">
                              & GENERAL SUPPLIES LIMITED
                            </p>
                            <div className="text-[10px] text-slate-500 space-y-0.5 font-medium leading-relaxed">
                              <p>Air Conditioning, Refrigeration & Electrical Engineering Specialists</p>
                              <p>123 Tech Avenue, Innovation City | contact@bts.com | +255 700 000 000</p>
                              <p>TIN: 123-456-789 | VRN: 987-654-321</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="px-4 py-1.5 bg-blue-950 text-white font-black text-xs uppercase tracking-widest rounded-lg inline-block shadow-sm">
                              PROFORMA INVOICE
                            </span>
                            <p className="text-xl font-black text-slate-900 font-mono mt-2">
                              #{matchedInvoice?.invoiceNumber || viewingBilledInvoice.proformaNo}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Details Section */}
                      <div className="grid grid-cols-2 gap-8 relative z-10 text-xs">
                        <div className="p-5 bg-slate-50/80 rounded-2xl border border-slate-200/80 space-y-2">
                          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Bill To Client:</h3>
                          <p className="font-black text-slate-900 text-lg leading-tight uppercase">
                            {viewingBilledInvoice.siteName || viewingBilledInvoice.clientName}
                          </p>
                          {clients.find(c => c.name === (viewingBilledInvoice.siteName || viewingBilledInvoice.clientName))?.address && (
                            <p className="text-slate-600 text-xs">
                              {clients.find(c => c.name === (viewingBilledInvoice.siteName || viewingBilledInvoice.clientName))?.address}
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col justify-center text-right space-y-2">
                          <div>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Date Issued</span>
                            <span className="font-bold text-slate-900 text-sm">{viewingBilledInvoice.billDate || new Date().toLocaleDateString('en-GB')}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Equipment / Scope</span>
                            <span className="font-extrabold text-blue-900 text-sm">{viewingBilledInvoice.machineType || 'Technical Services'}</span>
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Prepared / Billed By</span>
                            <span className="font-bold text-slate-800 text-sm">{viewingBilledInvoice.billedBy || 'Billing Staff'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Subject / RE Header */}
                      {(viewingBilledInvoice.clientRequest || matchedInvoice?.description) && (
                        <div className="p-3 bg-blue-50/60 border border-blue-100 rounded-xl text-center relative z-10">
                          <span className="text-[9px] font-black text-blue-800 uppercase tracking-widest block">Subject</span>
                          <p className="font-black text-slate-900 text-sm uppercase">
                            RE: {viewingBilledInvoice.clientRequest || matchedInvoice?.description}
                          </p>
                        </div>
                      )}

                      {/* Bill Line Items Table */}
                      <div className="overflow-x-auto rounded-xl border border-slate-200 relative z-10">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-slate-900 text-white font-black uppercase tracking-wider">
                              <th className="p-3.5 w-12 text-center">S/N</th>
                              <th className="p-3.5">Item / Service Description</th>
                              <th className="p-3.5 text-center">Qty</th>
                              <th className="p-3.5 text-right">Unit Price</th>
                              <th className="p-3.5 text-right">Total Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {itemsList.map((item, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                                <td className="p-3.5 text-center text-slate-400 font-bold">{item.serialNumber || (idx + 1)}</td>
                                <td className="p-3.5 text-slate-900 font-bold">{item.description}</td>
                                <td className="p-3.5 text-center text-slate-600">{item.quantity} {item.unit || 'pcs'}</td>
                                <td className="p-3.5 text-right text-slate-700 font-mono">{formatCurrency(Number(item.unitPrice) || 0)}</td>
                                <td className="p-3.5 text-right font-black text-slate-900 font-mono">{formatCurrency(Number(item.amount) || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-50 font-black border-t-2 border-slate-200 text-sm">
                              <td colSpan={4} className="p-3.5 uppercase text-right text-slate-700">Total Billed Amount (TZS):</td>
                              <td className="p-3.5 text-right text-emerald-800 font-mono">{formatCurrency(totalAmount)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Bank Account / Payment Info */}
                      <div className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100 text-xs space-y-2 relative z-10">
                        <h4 className="font-black text-blue-900 uppercase tracking-widest text-[11px]">
                          Payment Details & Bank Account
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-slate-700 font-medium">
                          <div>
                            <p><span className="font-bold text-slate-900">Bank Name:</span> CRDB Bank Ltd / NMB Bank</p>
                            <p><span className="font-bold text-slate-900">Account Name:</span> Bright Technical Systems & General Supplies Ltd</p>
                          </div>
                          <div>
                            <p><span className="font-bold text-slate-900">Account Number:</span> 0150234567800</p>
                            <p><span className="font-bold text-slate-900">Currency:</span> TZS | Branch: Dar es Salaam</p>
                          </div>
                        </div>
                      </div>

                      {/* Remarks */}
                      {viewingBilledInvoice.remarks && (
                        <div className="text-xs relative z-10">
                          <h4 className="font-extrabold text-slate-900 uppercase tracking-wider mb-1">Billing Remarks / Notes</h4>
                          <p className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-slate-700 font-medium">
                            {viewingBilledInvoice.remarks}
                          </p>
                        </div>
                      )}

                      {/* Signature Row */}
                      <div className="pt-8 border-t border-slate-200 grid grid-cols-2 gap-8 text-xs relative z-10">
                        <div>
                          <p className="text-slate-400 font-bold uppercase text-[10px]">Prepared By (Billing / Commercial):</p>
                          <div className="mt-8 border-b border-slate-400 w-48" />
                          <p className="font-extrabold text-slate-800 mt-1">{viewingBilledInvoice.billedBy || 'Billing Staff'}</p>
                        </div>
                        <div className="text-right flex flex-col items-end">
                          <p className="text-slate-400 font-bold uppercase text-[10px]">Authorized Signature & Stamp:</p>
                          <div className="mt-8 border-b border-slate-400 w-48" />
                          <p className="font-extrabold text-blue-950 mt-1">Bright Technical Systems</p>
                        </div>
                      </div>

                      {/* Footer Note */}
                      <div className="pt-4 border-t border-slate-100 text-center text-slate-400 text-[10px] font-medium uppercase tracking-widest relative z-10">
                        Thank you for doing business with Bright Technical Systems
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>

      <ServiceScheduleViewerModal
        isOpen={isScheduleViewerOpen}
        onClose={() => setIsScheduleViewerOpen(false)}
      />
    </div>
  );
};

function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}

export default BillingDashboard;
